import { useQuery, useMutation, useQueryClient } from 'react-query'
import axios from 'axios'

const API_BASE_URL = import.meta.env.DEV ? '/api' : '/leadsource/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

/**
 * Unread notification count for the Header bell badge. Polls every 30s —
 * same convention as useProspectStats in useApi.js.
 */
export const useUnreadNotificationCount = (enabled = true) => {
  return useQuery(
    ['notifications-unread-count'],
    async () => {
      const { data } = await api.get('/notifications/unread-count')
      return data.unread
    },
    { enabled, refetchInterval: 30000 }
  )
}

/**
 * Recent notifications for the bell dropdown.
 */
export const useNotificationsList = (enabled = true) => {
  return useQuery(
    ['notifications-list'],
    async () => {
      const { data } = await api.get('/notifications', { params: { page: 1, page_size: 20 } })
      return data
    },
    { enabled, staleTime: 10000 }
  )
}

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async (notificationId) => {
      const { data } = await api.post(`/notifications/${notificationId}/read`)
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
        queryClient.invalidateQueries({ queryKey: ['notifications-list'] })
      },
    }
  )
}

/**
 * True when this user is assigned to a campaign but has no mobile number
 * set to forward replies to — drives the "add your mobile" nagging banner.
 */
export const useNeedsMobile = (enabled = true) => {
  return useQuery(
    ['notifications-needs-mobile'],
    async () => {
      const { data } = await api.get('/notifications/needs-mobile')
      return data.needs_mobile
    },
    { enabled, staleTime: 5 * 60 * 1000 }
  )
}

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async () => {
      const { data } = await api.post('/notifications/read-all')
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
        queryClient.invalidateQueries({ queryKey: ['notifications-list'] })
      },
    }
  )
}
